const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getHeaders = () => {
  const authData = JSON.parse(localStorage.getItem('karma-auth') || '{}');
  const token = authData?.state?.token;
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

export const isClientViewMode = () => {
  const st = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state || {};
  return !!st.impersonation && st.user?.role === 'client';
};
const READ_ONLY_MSG = 'Modo vista de cliente: solo puedes mirar, no hacer cambios en su nombre.';

// Generic API call
const apiCall = async (endpoint, options = {}) => {
  if (options.method && options.method !== 'GET' && isClientViewMode()) throw new Error(READ_ONLY_MSG);
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...getHeaders(),
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error de conexión' }));
    throw new Error(error.detail || 'Error en la solicitud');
  }
  
  return response.json();
};

// Auth
export const authAPI = {
  login: (email, password) => apiCall('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }),
  register: (data) => apiCall('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  me: () => apiCall('/auth/me'),
  forgotPassword: (email) => apiCall('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
  changePassword: (current_password, new_password) => apiCall('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password, new_password }),
  }),
};

// Profile
export const profileAPI = {
  update: (data) => apiCall('/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  completeOnboarding: (step, data) => apiCall('/onboarding/complete', {
    method: 'POST',
    body: JSON.stringify({ step, data }),
  }),
};

// Team
export const teamAPI = {
  getAll: () => apiCall('/team'),
  getMember: (id) => apiCall(`/team/${id}`),
  update: (id, data) => apiCall(`/team/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  updateDirectory: (id, data) => apiCall(`/team/${id}/directory`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  setTimeMethod: (id, method) => apiCall(`/team/${id}/time-method`, {
    method: 'PUT',
    body: JSON.stringify({ method }),
  }),
  getHierarchy: () => apiCall('/team/hierarchy'),
};

// Freelancers
export const freelancersAPI = {
  getAll: () => apiCall('/freelancers'),
  create: (data) => apiCall('/freelancers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/freelancers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/freelancers/${id}`, { method: 'DELETE' }),
};

// Work Hours (registro manual)
export const workHoursAPI = {
  getAll: () => apiCall('/work-hours'),
  getSummary: () => apiCall('/work-hours/summary'),
  create: (data) => apiCall('/work-hours', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/work-hours/${id}`, { method: 'DELETE' }),
};

// Projects
export const projectsAPI = {
  getAll: () => apiCall('/projects'),
  get: (id) => apiCall(`/projects/${id}`),
  create: (data) => apiCall('/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Tasks
export const tasksAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tasks${query ? `?${query}` : ''}`);
  },
  getArchived: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/tasks/archived${query ? `?${query}` : ''}`);
  },
  getMine: () => apiCall('/tasks/me'),
  get: (id) => apiCall(`/tasks/${id}`),
  create: (data) => apiCall('/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/tasks/${id}`, { method: 'DELETE' }),
  addTimeLog: (id, durationMinutes, description = null) => apiCall(`/tasks/${id}/time-logs`, {
    method: 'POST',
    body: JSON.stringify({ task_id: id, duration_minutes: durationMinutes, description }),
  }),
  getTimeLogs: (id) => apiCall(`/tasks/${id}/time-logs`),
};

// Time tracking (resumen mensual de horas)
export const timeTrackingAPI = {
  getMonthly: (month) => apiCall(`/time-tracking/monthly${month ? `?month=${month}` : ''}`),
  getMyMonthly: (month) => apiCall(`/time-tracking/my-monthly${month ? `?month=${month}` : ''}`),
  updateLog: (logId, durationMinutes) => apiCall(`/time-logs/${logId}`, {
    method: 'PUT',
    body: JSON.stringify({ duration_minutes: durationMinutes }),
  }),
  deleteLog: (logId) => apiCall(`/time-logs/${logId}`, { method: 'DELETE' }),
  // Método/config del usuario actual
  getConfig: () => apiCall('/time-tracking/config'),
  // Resumen unificado (miembro)
  getMe: (from, to) => apiCall(`/time-tracking/me${from && to ? `?date_from=${from}&date_to=${to}` : ''}`),
  // Vista global CEO
  getGlobal: ({ from, to, userId, clientId } = {}) => {
    const p = new URLSearchParams();
    if (from) p.append('date_from', from);
    if (to) p.append('date_to', to);
    if (userId) p.append('user_id', userId);
    if (clientId) p.append('client_id', clientId);
    const qs = p.toString();
    return apiCall(`/time-tracking/global${qs ? `?${qs}` : ''}`);
  },
  // Detalle de un miembro (CEO abre su ficha de horas)
  getMemberDetail: (userId, from, to) => apiCall(`/time-tracking/member/${userId}${from && to ? `?date_from=${from}&date_to=${to}` : ''}`),
  // Quién tiene el cronómetro/jornada activa ahora (CEO)
  getActiveNow: () => apiCall('/time-tracking/active-now'),
  // Filas del periodo para exportar CSV (CEO)
  getExport: (from, to) => apiCall(`/time-tracking/export?date_from=${from}&date_to=${to}`),
  // Marca de cronómetro activo en servidor (indicador en vivo)
  timerStart: (taskId) => apiCall(`/tasks/${taskId}/timer/start`, { method: 'POST' }),
  timerStop: (taskId) => apiCall(`/tasks/${taskId}/timer/stop`, { method: 'POST' }),
};

// Rentabilidad por cliente (solo CEOs)
export const profitabilityAPI = {
  get: (from, to) => apiCall(`/finance/profitability${from && to ? `?date_from=${from}&date_to=${to}` : ''}`),
  getRates: () => apiCall('/finance/profitability/rates'),
  saveRates: (data) => apiCall('/finance/profitability/rates', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Gastos recurrentes y previsión (banco)
export const recurringExpensesAPI = {
  list: () => apiCall('/finance/recurring-expenses'),
  create: (data) => apiCall('/finance/recurring-expenses', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/finance/recurring-expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  remove: (id) => apiCall(`/finance/recurring-expenses/${id}`, { method: 'DELETE' }),
  forecast: (months = 3) => apiCall(`/finance/expense-forecast?months=${months}`),
};

// Facturas de gasto (Google Drive) vinculadas a movimientos del banco
export const expenseInvoicesAPI = {
  status: (month) => apiCall(`/finance/expense-invoices/status${month ? `?month=${month}` : ''}`),
  files: (folderId) => apiCall(`/finance/expense-invoices/files${folderId ? `?folder_id=${folderId}` : ''}`),
  setFolder: (folder) => apiCall('/finance/expense-invoices/folder', {
    method: 'PUT',
    body: JSON.stringify({ folder }),
  }),
  link: (data) => apiCall('/finance/expense-invoices/link', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  unlink: (txKey) => apiCall(`/finance/expense-invoices/link/${encodeURIComponent(txKey)}`, { method: 'DELETE' }),
};

// Previsión de tesorería
export const treasuryAPI = {
  forecast: () => apiCall('/finance/treasury-forecast'),
};

// Fichaje de jornada (Método A)
export const timeClockAPI = {
  getActive: () => apiCall('/time-clock/active'),
  start: () => apiCall('/time-clock/start', { method: 'POST' }),
  pause: () => apiCall('/time-clock/pause', { method: 'POST' }),
  resume: () => apiCall('/time-clock/resume', { method: 'POST' }),
  stop: () => apiCall('/time-clock/stop', { method: 'POST' }),
  getSessions: (from, to) => apiCall(`/time-clock/sessions${from && to ? `?date_from=${from}&date_to=${to}` : ''}`),
  editSession: (id, data) => apiCall(`/time-clock/sessions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSession: (id) => apiCall(`/time-clock/sessions/${id}`, { method: 'DELETE' }),
};

// Content workflow settings (scheduler config)
export const workflowSettingsAPI = {
  get: () => apiCall('/settings/content-workflow'),
  update: (data) => apiCall('/settings/content-workflow', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Journal
export const journalAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/journal${query ? `?${query}` : ''}`);
  },
  get: (date) => apiCall(`/journal/${date}`),
  save: (data) => apiCall('/journal', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Work Journal (diario de trabajo personal del miembro — privado)
export const workJournalAPI = {
  list: () => apiCall('/work-journal'),
  create: (data) => apiCall('/work-journal', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/work-journal/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/work-journal/${id}`, { method: 'DELETE' }),
};

// Habits
export const habitsAPI = {
  getAll: () => apiCall('/habits'),
  create: (data) => apiCall('/habits', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  log: (habitId, date, completed = true) => apiCall('/habits/log', {
    method: 'POST',
    body: JSON.stringify({ habit_id: habitId, date, completed }),
  }),
  delete: (habitId) => apiCall(`/habits/${habitId}`, { method: 'DELETE' }),
};

// Leads/CRM
export const leadsAPI = {
  getAll: (status = null) => apiCall(`/leads${status ? `?status=${status}` : ''}`),
  create: (data) => apiCall('/leads', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/leads/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Invoices
export const invoicesAPI = {
  getAll: () => apiCall('/invoices'),
  create: (data) => apiCall('/invoices', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateStatus: (id, status) => apiCall(`/invoices/${id}/status?status=${status}`, {
    method: 'PUT',
  }),
};

// Documents
export const documentsAPI = {
  getAll: (type = null) => apiCall(`/documents${type ? `?type=${type}` : ''}`),
  create: (data) => apiCall('/documents', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// SOPs
export const sopsAPI = {
  getAll: (category = null) => apiCall(`/sops${category ? `?category=${category}` : ''}`),
  create: (data) => apiCall('/sops', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/sops/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  toggleFavorite: (id) => apiCall(`/sops/${id}/favorite`, { method: 'POST' }),
};

// Notifications
export const notificationsAPI = {
  getAll: () => apiCall('/notifications'),
  markAsRead: (id) => apiCall(`/notifications/${id}/read`, { method: 'PUT' }),
  markAllAsRead: () => apiCall('/notifications/read-all', { method: 'PUT' }),
  remove: (id) => apiCall(`/notifications/${id}`, { method: 'DELETE' }),
  getDiscordConfig: () => apiCall('/notifications/discord-config'),
  updateDiscordConfig: (types) => apiCall('/notifications/discord-config', { method: 'PUT', body: JSON.stringify({ types }) }),
};

// Time Off
export const timeOffAPI = {
  getAll: () => apiCall('/time-off'),
  request: (data) => apiCall('/time-off', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  remove: (id) => apiCall(`/time-off/${id}`, { method: 'DELETE' }),
};

export const feedbackAPI = {
  getMeta: () => apiCall('/feedback/meta'),
  submit: (data) => apiCall('/feedback', { method: 'POST', body: JSON.stringify(data) }),
  getMine: () => apiCall('/feedback/mine'),
  getAll: ({ category, status } = {}) => {
    const p = new URLSearchParams();
    if (category && category !== '__all') p.append('category', category);
    if (status && status !== '__all') p.append('status', status);
    const qs = p.toString();
    return apiCall(`/feedback${qs ? `?${qs}` : ''}`);
  },
  setStatus: (id, status) => apiCall(`/feedback/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  reply: (id, reply) => apiCall(`/feedback/${id}/reply`, { method: 'PUT', body: JSON.stringify({ reply }) }),
};

// Events
export const eventsAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/events${query ? `?${query}` : ''}`);
  },
  create: (data) => apiCall('/events', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Finance
export const financeAPI = {
  getTransactions: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/transactions${query ? `?${query}` : ''}`);
  },
  createTransaction: (data) => apiCall('/transactions', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getSummary: () => apiCall('/finance/summary'),
  getConfig: () => apiCall('/finance/config'),
  updateConfig: (data) => apiCall('/finance/config', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getDashboardSummary: () => apiCall('/finance/dashboard-summary'),
  getDiscordWebhook: () => apiCall('/finance/discord-webhook'),
  setDiscordWebhook: (url) => apiCall('/finance/discord-webhook', { method: 'PUT', body: JSON.stringify({ url }) }),
  testDiscordWebhook: () => apiCall('/finance/discord-webhook/test', { method: 'POST' }),
  getBankConsent: () => apiCall('/bank/consent'),
  setBankConsent: (consent_date, valid_days) => apiCall('/bank/consent', { method: 'PUT', body: JSON.stringify({ consent_date, valid_days }) }),
};

// KPIs
export const kpisAPI = {
  getCEO: () => apiCall('/kpis/ceo'),
  getMember: () => apiCall('/kpis/member'),
  getCEODashboardReal: (month = null) => apiCall(`/dashboard/ceo/real${month ? `?month=${month}` : ''}`),
  refreshAllDashboard: () => apiCall('/dashboard/refresh-all', { method: 'POST' }),
};

// Agenda interna (agregador del día + eventos personales)
export const agendaAPI = {
  getToday: () => apiCall('/agenda/today'),
  getMonth: (month) => apiCall(`/agenda/month${month ? `?month=${month}` : ''}`),
  getRange: (from, to) => apiCall(`/agenda/range?date_from=${from}&date_to=${to}`),
  listPersonalEvents: (fecha = null) => apiCall(`/agenda/personal-events${fecha ? `?fecha=${fecha}` : ''}`),
  createPersonalEvent: (data) => apiCall('/agenda/personal-events', { method: 'POST', body: JSON.stringify(data) }),
  updatePersonalEvent: (id, data) => apiCall(`/agenda/personal-events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePersonalEvent: (id) => apiCall(`/agenda/personal-events/${id}`, { method: 'DELETE' }),
};

// AI
export const aiAPI = {
  analyze: (prompt, context = null) => apiCall('/ai/analyze', {
    method: 'POST',
    body: JSON.stringify({ prompt, context }),
  }),
  transcribe: async (file) => {
    const authData = JSON.parse(localStorage.getItem('karma-auth') || '{}');
    const token = authData?.state?.token;
    
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/ai/transcribe`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Error en transcripción');
    }
    
    return response.json();
  },
  analyzeImage: async (file, prompt) => {
    const authData = JSON.parse(localStorage.getItem('karma-auth') || '{}');
    const token = authData?.state?.token;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('prompt', prompt);
    
    const response = await fetch(`${API_URL}/ai/analyze-image`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Error en análisis de imagen');
    }
    
    return response.json();
  },
};

// Seed

// ============ NEW APIs FOR PERSISTENCE ============

// Clients (CRM)
export const clientsAPI = {
  getAll: () => apiCall('/clients'),
  get: (id) => apiCall(`/clients/${id}`),
  create: (data) => apiCall('/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/clients/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/clients/${id}`, { method: 'DELETE' }),
};

// Prospects (CRM)
export const prospectsAPI = {
  getAll: (status = null) => apiCall(`/prospects${status ? `?status=${status}` : ''}`),
  getFromSheet: (forceRefresh = false) => apiCall(`/prospects/from-sheet${forceRefresh ? '?force_refresh=true' : ''}`),
  create: (data) => apiCall('/prospects', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/prospects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/prospects/${id}`, { method: 'DELETE' }),
};


// Content Calendar
export const contentCalendarAPI = {
  states: () => apiCall(`/content-calendar/states`),
  workload: () => apiCall(`/content-calendar/workload`),
  getAll: (clientId = null) => apiCall(`/content-calendar${clientId ? `?client_id=${clientId}` : ''}`),
  get: (id) => apiCall(`/content-calendar/${id}`),
  create: (data) => apiCall('/content-calendar', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/content-calendar/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/content-calendar/${id}`, { method: 'DELETE' }),
  approve: (id) => apiCall(`/content-calendar/${id}/approve`, { method: 'POST' }),
  markViewed: (id) => apiCall(`/content-calendar/${id}/viewed`, { method: 'POST' }),
  bulkApprove: (ids) => apiCall('/content-calendar-approvals/bulk', { method: 'POST', body: JSON.stringify({ ids }) }),
  approvalsOverview: () => apiCall('/content-calendar-approvals/overview'),
  assignChanges: (id, user_id, nota) => apiCall(`/content-calendar/${id}/assign-changes`, { method: 'POST', body: JSON.stringify({ user_id, nota }) }),
  requestChanges: (id, texto) => apiCall(`/content-calendar/${id}/request-changes`, {
    method: 'POST',
    body: JSON.stringify({ texto }),
  }),
  sendApprovalReminder: (clientId) => apiCall(`/content-calendar/approval-reminder/${clientId}`, { method: 'POST' }),
  addObservation: (id, texto) => apiCall(`/content-calendar/${id}/observations`, {
    method: 'POST',
    body: JSON.stringify({ texto }),
  }),
  listAttachments: (id) => apiCall(`/content-calendar/${id}/attachments`),
  deleteAttachment: (contentId, fileId) =>
    apiCall(`/content-calendar/${contentId}/attachments/${fileId}`, { method: 'DELETE' }),
  getAttachmentUrl: (contentId, fileId) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    return `${process.env.REACT_APP_BACKEND_URL}/api/content-calendar/${contentId}/attachments/${fileId}/download?auth=${encodeURIComponent(token)}`;
  },
  getAttachmentDownloadUrl: (contentId, fileId) => `${contentCalendarAPI.getAttachmentUrl(contentId, fileId)}&download=1`,
  downloadZip: async (ids) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    const res = await fetch(`${API_URL}/content-calendar-download/zip?ids=${encodeURIComponent(ids.join(','))}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error generando el ZIP'); }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''([^;]+)/);
    const name = m ? decodeURIComponent(m[1]) : 'material.zip';
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  },
  uploadAttachment: async (contentId, file, onProgress) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/content-calendar/${contentId}/attachments`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.detail || 'Error subiendo archivo'));
          } catch {
            reject(new Error('Error subiendo archivo'));
          }
        }
      };
      xhr.onerror = () => reject(new Error('Error de red'));
      const fd = new FormData();
      fd.append('file', file);
      xhr.send(fd);
    });
  },
  importCsv: async (clientId, file, dryRun, notifyClients = false) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token;
    const fd = new FormData();
    fd.append('client_id', clientId);
    fd.append('dry_run', dryRun ? 'true' : 'false');
    fd.append('notify_clients', notifyClients ? 'true' : 'false');
    fd.append('file', file);
    const res = await fetch(`${API_URL}/content-calendar/import`, {
      method: 'POST', headers: { Authorization: token ? `Bearer ${token}` : '' }, body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'No se pudo leer el CSV');
    return data;
  },
  addComment: async (id, comment) => {
    const authData = JSON.parse(localStorage.getItem('karma-auth') || '{}');
    const token = authData?.state?.token;
    const formData = new FormData();
    formData.append('comment', comment);
    const response = await fetch(`${API_URL}/content-calendar/${id}/comment`, {
      method: 'POST',
      headers: { 'Authorization': token ? `Bearer ${token}` : '' },
      body: formData,
    });
    if (!response.ok) throw new Error('Error al añadir comentario');
    return response.json();
  },
};

// Campaigns
export const campaignsAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/campaigns${query ? `?${query}` : ''}`);
  },
  get: (id) => apiCall(`/campaigns/${id}`),
  create: (data) => apiCall('/campaigns', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/campaigns/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/campaigns/${id}`, { method: 'DELETE' }),
};

// Reports
export const reportsAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/reports${query ? `?${query}` : ''}`);
  },
  create: (data) => apiCall('/reports', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Bank Accounts
export const bankAccountsAPI = {
  getAll: () => apiCall('/bank-accounts'),
  create: (data) => apiCall('/bank-accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/bank-accounts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Expenses
export const expensesAPI = {
  getAll: (categoria = null) => apiCall(`/expenses${categoria ? `?categoria=${categoria}` : ''}`),
  create: (data) => apiCall('/expenses', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/expenses/${id}`, { method: 'DELETE' }),
};

// Incomes
export const incomesAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/incomes${query ? `?${query}` : ''}`);
  },
  create: (data) => apiCall('/incomes', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/incomes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Investments
export const investmentsAPI = {
  getAll: () => apiCall('/investments'),
  create: (data) => apiCall('/investments', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  payQuota: (id) => apiCall(`/investments/${id}/pay-quota`, { method: 'PUT' }),
};

// Task Comments
export const taskCommentsAPI = {
  getAll: (taskId) => apiCall(`/tasks/${taskId}/comments`),
  create: (taskId, content) => apiCall(`/tasks/${taskId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ task_id: taskId, content }),
  }),
};

// Task Time Logs
export const taskTimeLogsAPI = {
  getAll: (taskId) => apiCall(`/tasks/${taskId}/time-logs`),
  create: (taskId, durationMinutes, description = null) => apiCall(`/tasks/${taskId}/time-logs`, {
    method: 'POST',
    body: JSON.stringify({ task_id: taskId, duration_minutes: durationMinutes, description }),
  }),
};

// Task Attachments
export const taskAttachmentsAPI = {
  getAll: (taskId) => apiCall(`/tasks/${taskId}/attachments`),
  create: (taskId, filename, url, fileType = null) => apiCall(`/tasks/${taskId}/attachments`, {
    method: 'POST',
    body: JSON.stringify({ task_id: taskId, filename, url, file_type: fileType }),
  }),
  delete: (taskId, attachmentId) => apiCall(`/tasks/${taskId}/attachments/${attachmentId}`, { method: 'DELETE' }),
};

// Nango Integrations
export const meetingsAPI = {
  listForClient: (clientId) => apiCall(`/service-delivery/clients/${clientId}/meetings`),
  getContacts: (clientId) => apiCall(`/service-delivery/clients/${clientId}/contacts`),
  create: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/meetings`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/meetings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/meetings/${id}`, { method: 'DELETE' }),
  syncGoogle: () => apiCall('/meetings/sync-google', { method: 'POST' }),
};

export const gcalAPI = {
  status: () => apiCall('/oauth/calendar/status'),
  login: () => apiCall('/oauth/calendar/login'),
  disconnect: () => apiCall('/oauth/calendar/disconnect', { method: 'POST' }),
};

export const collabAPI = {
  myClients: () => apiCall('/collab/me/clients'),
  content: (clientId) => apiCall(`/collab/clients/${clientId}/content`),
  recordings: (clientId) => apiCall(`/collab/clients/${clientId}/recordings`),
  brandKit: (clientId) => apiCall(`/collab/clients/${clientId}/brand-kit`),
  markRecorded: (contentId, nota) => apiCall(`/collab/content/${contentId}/mark-recorded`, { method: 'POST', body: JSON.stringify({ nota: nota || null }) }),
  adminList: () => apiCall('/collab/admin/list'),
  adminInvite: (data) => apiCall('/collab/admin/invite', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdate: (id, data) => apiCall(`/collab/admin/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDelete: (id) => apiCall(`/collab/admin/${id}`, { method: 'DELETE' }),
  adminResend: (id) => apiCall(`/collab/admin/${id}/resend-invite`, { method: 'POST' }),
};

export const calendarsAPI = {
  teamRange: (from, to) => apiCall(`/calendars/team/range?date_from=${from}&date_to=${to}`),
  teamMembers: () => apiCall('/calendars/team/members'),
  createTeamMeeting: (data) => apiCall('/calendars/team/meetings', { method: 'POST', body: JSON.stringify(data) }),
  updateTeamMeeting: (id, data) => apiCall(`/calendars/team/meetings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTeamMeeting: (id) => apiCall(`/calendars/team/meetings/${id}`, { method: 'DELETE' }),
  restoreTeamMeeting: (id) => apiCall(`/calendars/team/meetings/${id}/restore`, { method: 'POST' }),
  ceoRange: (from, to) => apiCall(`/calendars/ceo/range?date_from=${from}&date_to=${to}`),
  teamAvailability: (from, to) => apiCall(`/calendars/ceo/team-availability?date_from=${from}&date_to=${to}`),
  createCeoEvent: (data) => apiCall('/calendars/ceo/events', { method: 'POST', body: JSON.stringify(data) }),
  updateCeoEvent: (id, data) => apiCall(`/calendars/ceo/events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCeoEvent: (id) => apiCall(`/calendars/ceo/events/${id}`, { method: 'DELETE' }),
  restoreCeoEvent: (id) => apiCall(`/calendars/ceo/events/${id}/restore`, { method: 'POST' }),
  setTeamShare: (id, para_equipo, todas = true) => apiCall(`/calendars/ceo/events/${id}/team-share`, { method: 'PUT', body: JSON.stringify({ para_equipo, todas }) }),
  getReminderSettings: () => apiCall('/calendars/reminder-settings'),
  setReminderSettings: (minutos) => apiCall('/calendars/reminder-settings', { method: 'PUT', body: JSON.stringify({ minutos }) }),
  googleStatus: () => apiCall('/calendars/google/status'),
  googleCalendars: () => apiCall('/calendars/google/calendars'),
  googleSettings: (data) => apiCall('/calendars/google/settings', { method: 'PUT', body: JSON.stringify(data) }),
  googleSyncNow: () => apiCall('/calendars/google/sync-now', { method: 'POST' }),
};

export const nangoAPI = {
  createSession: (allowedIntegrations = ['facebook', 'slack', 'google-calendar', 'google-drive', 'notion', 'hubspot']) =>
    apiCall('/nango/session', {
      method: 'POST',
      body: JSON.stringify({ allowed_integrations: allowedIntegrations }),
    }),
  getConnections: () => apiCall('/nango/connections'),
  getConnection: (integrationId) => apiCall(`/nango/connections/${integrationId}`),
  storeConnection: (integrationId, connectionId) => apiCall('/nango/connections/store', {
    method: 'POST',
    body: JSON.stringify({ integration_id: integrationId, connection_id: connectionId }),
  }),
  disconnect: (integrationId) => apiCall(`/nango/connections/${integrationId}`, { method: 'DELETE' }),
};

// Facebook/Meta API
export const facebookAPI = {
  connect: (connectionId, clientId = null) => apiCall('/facebook/connect', {
    method: 'POST',
    body: JSON.stringify({ connection_id: connectionId, client_id: clientId }),
  }),
  getPages: (connectionId = null) => apiCall(`/facebook/pages${connectionId ? `?connection_id=${connectionId}` : ''}`),
  getPageInsights: (pageId, period = 'day') => apiCall(`/facebook/pages/${pageId}/insights?period=${period}`),
  getPagePosts: (pageId, limit = 25) => apiCall(`/facebook/pages/${pageId}/posts?limit=${limit}`),
  createPost: (pageId, message, scheduledTime = null, link = null) => apiCall(`/facebook/pages/${pageId}/posts`, {
    method: 'POST',
    body: JSON.stringify({ 
      page_id: pageId, 
      message, 
      scheduled_publish_time: scheduledTime,
      link 
    }),
  }),
  getMessages: (pageId) => apiCall(`/facebook/pages/${pageId}/messages`),
  replyToMessage: (conversationId, pageId, message) => apiCall(`/facebook/messages/${conversationId}/reply?page_id=${pageId}`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  getComments: (pageId) => apiCall(`/facebook/pages/${pageId}/comments`),
  replyToComment: (commentId, pageId, message) => apiCall(`/facebook/comments/${commentId}/reply?page_id=${pageId}`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  getInstagramMedia: (igAccountId) => apiCall(`/facebook/instagram/${igAccountId}/media`),
};

// Bank Data from Google Sheets
export const bankDataAPI = {
  getAccounts: () => apiCall('/bank/accounts'),
  getTransactions: (limit = 100, category = null) => 
    apiCall(`/bank/transactions?limit=${limit}${category ? `&category=${category}` : ''}`),
  getSummary: () => apiCall('/bank/summary'),
  getTransactionTags: () => apiCall('/bank/transaction-tags'),
  saveTransactionTag: (txKey, category) => apiCall('/bank/transaction-tags', {
    method: 'PUT',
    body: JSON.stringify({ tx_key: txKey, category }),
  }),
  saveTransactionCategories: (categories) => apiCall('/bank/transaction-tags/categories', {
    method: 'PUT',
    body: JSON.stringify({ categories }),
  }),
};

// Manual Payroll
export const payrollAPI = {
  getAll: () => apiCall('/finance/payroll'),
  save: (yearMonth, amount) => apiCall('/finance/payroll', {
    method: 'PUT',
    body: JSON.stringify({ year_month: yearMonth, amount }),
  }),
};

// Quipu API
export const quipuAPI = {
  getStatus: () => apiCall('/quipu/status'),
  getInvoices: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/quipu/invoices${query ? `?${query}` : ''}`);
  },
  getTickets: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/quipu/tickets${query ? `?${query}` : ''}`);
  },
  getPaysheets: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/quipu/paysheets${query ? `?${query}` : ''}`);
  },
  getBookEntries: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/quipu/book_entries${query ? `?${query}` : ''}`);
  },
  getContacts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/quipu/contacts${query ? `?${query}` : ''}`);
  },
  getAccountingCategories: (kind = null) => 
    apiCall(`/quipu/accounting_categories${kind ? `?kind=${kind}` : ''}`),
  getFinanceSummary: (year = null) => 
    apiCall(`/quipu/finance-summary${year ? `?year=${year}` : ''}`),
  getDashboardData: () => apiCall('/quipu/dashboard-data'),
  clearCache: () => apiCall('/quipu/clear-cache', { method: 'POST' }),
};

// Service Delivery API
export const serviceDeliveryAPI = {
  getClients: (estado) => apiCall(`/service-delivery/clients${estado ? `?estado=${estado}` : ''}`),
  archiveClient: (id, body) => apiCall(`/service-delivery/clients/${id}/archive`, { method: 'POST', body: JSON.stringify(body) }),
  reactivateClient: (id) => apiCall(`/service-delivery/clients/${id}/reactivate`, { method: 'POST' }),
  getClient: (clientId) => apiCall(`/service-delivery/clients/${clientId}`),
  // Documentos e informes por cliente (admin)
  listClientDocuments: (clientId) => apiCall(`/service-delivery/clients/${clientId}/documents`),
  deleteClientDocument: (clientId, docId) => apiCall(`/service-delivery/clients/${clientId}/documents/${docId}`, { method: 'DELETE' }),
  getClientActivity: (clientId) => apiCall(`/service-delivery/clients/${clientId}/activity`),
  testDiscordWebhook: (clientId, url) => apiCall(`/service-delivery/clients/${clientId}/discord-test`, { method: 'POST', body: JSON.stringify({ url }) }),
  uploadClientDocument: async (clientId, { file, nombre, categoria, mes }) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    const fd = new FormData();
    fd.append('file', file);
    fd.append('nombre', nombre || file.name);
    fd.append('categoria', categoria || 'documento');
    if (mes) fd.append('mes', mes);
    const res = await fetch(`${API_URL}/service-delivery/clients/${clientId}/documents`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo documento'); }
    return res.json();
  },
  setQuipuContact: (clientId, quipuContactId) => apiCall(`/service-delivery/clients/${clientId}/quipu-contact`, {
    method: 'PUT', body: JSON.stringify({ quipu_contact_id: quipuContactId || null }),
  }),
  getQuipuContacts: () => apiCall('/quipu/contacts'),
  getAllQuipuContacts: () => apiCall('/quipu/contacts/all'),
  getMetaAdAccounts: () => apiCall('/meta/ad-accounts'),
  setMetaAccount: (clientId, metaAdAccountId) => apiCall(`/service-delivery/clients/${clientId}/meta-account`, {
    method: 'PUT', body: JSON.stringify({ meta_ad_account_id: metaAdAccountId || null }),
  }),
  createClient: (data) => apiCall('/service-delivery/clients', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateClient: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  addClientLog: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/log`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  deleteClientLog: (clientId, entryId) => apiCall(`/service-delivery/clients/${clientId}/log/${entryId}`, {
    method: 'DELETE'
  }),
  saveBriefing: (clientId, payload) => apiCall(`/service-delivery/clients/${clientId}/briefing`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  }),
  setBriefingStatus: (clientId, status) => apiCall(`/service-delivery/clients/${clientId}/briefing/status`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  }),
  remindBriefing: (clientId) => apiCall(`/service-delivery/clients/${clientId}/briefing/remind`, {
    method: 'POST',
    body: JSON.stringify({})
  }),
  getDriveFolders: (clientId) => apiCall(`/service-delivery/clients/${clientId}/drive-folders`),
  saveDriveFolders: (clientId, folders) => apiCall(`/service-delivery/clients/${clientId}/drive-folders`, {
    method: 'PUT',
    body: JSON.stringify({ folders })
  }),
  getBusinessInfo: (clientId) => apiCall(`/service-delivery/clients/${clientId}/business-info`),
  getBuyerPersona: (clientId) => apiCall(`/service-delivery/clients/${clientId}/buyer-persona`),
  saveBuyerPersona: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/buyer-persona`, {
    method: 'PUT',
    body: JSON.stringify({ data })
  }),
  extractBuyerPersona: (clientId) => apiCall(`/service-delivery/clients/${clientId}/buyer-persona/extract-from-plan`, {
    method: 'POST'
  }),
  saveBusinessInfo: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/business-info`, {
    method: 'PUT',
    body: JSON.stringify({ data })
  }),
  getBusinessStrategy: (clientId) => apiCall(`/service-delivery/clients/${clientId}/business-strategy`),
  saveBusinessStrategy: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/business-strategy`, {
    method: 'PUT',
    body: JSON.stringify({ data })
  }),
  getStrategy: (clientId, kind) => apiCall(`/service-delivery/clients/${clientId}/strategy/${kind}`),
  saveStrategy: (clientId, kind, data) => apiCall(`/service-delivery/clients/${clientId}/strategy/${kind}`, {
    method: 'PUT',
    body: JSON.stringify({ data })
  }),
  getLibrary: (clientId, kind) => apiCall(`/service-delivery/clients/${clientId}/library/${kind}`),
  saveLibrary: (clientId, kind, data) => apiCall(`/service-delivery/clients/${clientId}/library/${kind}`, {
    method: 'PUT',
    body: JSON.stringify({ data })
  }),
  getMetaCampaigns: (clientId, month) => apiCall(`/service-delivery/clients/${clientId}/meta-campaigns${month ? `?month=${month}` : ''}`),
  listCampaignOptimizations: (clientId) => apiCall(`/service-delivery/clients/${clientId}/campaign-optimizations`),
  createCampaignOptimization: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/campaign-optimizations`, {
    method: 'POST', body: JSON.stringify(data)
  }),
  updateCampaignOptimization: (clientId, optId, data) => apiCall(`/service-delivery/clients/${clientId}/campaign-optimizations/${optId}`, {
    method: 'PUT', body: JSON.stringify(data)
  }),
  captureOptimizationAfter: (clientId, optId) => apiCall(`/service-delivery/clients/${clientId}/campaign-optimizations/${optId}/capture-after`, { method: 'POST' }),
  deleteCampaignOptimization: (clientId, optId) => apiCall(`/service-delivery/clients/${clientId}/campaign-optimizations/${optId}`, { method: 'DELETE' }),
  globalCampaignOptimizations: () => apiCall('/campaign-optimizations/global'),
  getCopies: (clientId) => apiCall(`/service-delivery/clients/${clientId}/copies`),
  saveCopies: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/copies`, {
    method: 'PUT',
    body: JSON.stringify({ data })
  }),
  getObjectives: (clientId) => apiCall(`/service-delivery/clients/${clientId}/objectives`),
  saveObjectives: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/objectives`, {
    method: 'PUT',
    body: JSON.stringify({ data })
  }),
  metricoolBrands: (clientId) => apiCall(`/service-delivery/clients/${clientId}/metricool/brands`),
  getMetricoolMapping: (clientId) => apiCall(`/service-delivery/clients/${clientId}/metricool/mapping`),
  setMetricoolMapping: (clientId, blog_id, brand_name) => apiCall(`/service-delivery/clients/${clientId}/metricool/mapping`, {
    method: 'PUT', body: JSON.stringify({ blog_id, brand_name })
  }),
  listTypedReports: (clientId, rtype) => apiCall(`/service-delivery/clients/${clientId}/reports/${rtype}`),
  getTypedReport: (clientId, rtype, month) => apiCall(`/service-delivery/clients/${clientId}/reports/${rtype}/${month}`),
  generateTypedReport: (clientId, rtype, month) => apiCall(`/service-delivery/clients/${clientId}/reports/${rtype}/generate`, {
    method: 'POST', body: JSON.stringify({ month })
  }),
  updateTypedReport: (clientId, rtype, month, payload) => apiCall(`/service-delivery/clients/${clientId}/reports/${rtype}/${month}`, {
    method: 'PUT', body: JSON.stringify(payload)
  }),
  uploadReportPdf: async (clientId, rtype, month, file) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/service-delivery/clients/${clientId}/reports/${rtype}/${month}/pdf`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo el PDF'); }
    return res.json();
  },
  deleteReportPdf: (clientId, rtype, month) => apiCall(`/service-delivery/clients/${clientId}/reports/${rtype}/${month}/pdf`, { method: 'DELETE' }),
  getAdsSheet: (clientId) => apiCall(`/service-delivery/clients/${clientId}/ads-optimization-sheet`),
  setAdsSheet: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/ads-optimization-sheet`, { method: 'PUT', body: JSON.stringify(data) }),
  listWeeklyActions: (clientId, archived = false) => apiCall(`/service-delivery/clients/${clientId}/weekly-actions${archived ? '?archived=true' : ''}`),
  createWeeklyAction: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/weekly-actions`, {
    method: 'POST', body: JSON.stringify(data)
  }),
  updateWeeklyAction: (clientId, actionId, data) => apiCall(`/service-delivery/clients/${clientId}/weekly-actions/${actionId}`, {
    method: 'PUT', body: JSON.stringify(data)
  }),
  deleteWeeklyAction: (clientId, actionId) => apiCall(`/service-delivery/clients/${clientId}/weekly-actions/${actionId}`, {
    method: 'DELETE'
  }),
  createSubtask: (clientId, actionId, data) => apiCall(`/service-delivery/clients/${clientId}/weekly-actions/${actionId}/subtasks`, {
    method: 'POST', body: JSON.stringify(data)
  }),
  updateSubtask: (clientId, actionId, subtaskId, data) => apiCall(`/service-delivery/clients/${clientId}/weekly-actions/${actionId}/subtasks/${subtaskId}`, {
    method: 'PUT', body: JSON.stringify(data)
  }),
  deleteSubtask: (clientId, actionId, subtaskId) => apiCall(`/service-delivery/clients/${clientId}/weekly-actions/${actionId}/subtasks/${subtaskId}`, {
    method: 'DELETE'
  }),
  uploadBriefingFile: async (clientId, file) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/service-delivery/clients/${clientId}/briefing/upload`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo archivo'); }
    return res.json();
  },
  extractBriefingFromFile: async (clientId, file, schema) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    const fd = new FormData();
    fd.append('file', file);
    fd.append('schema', JSON.stringify(schema));
    const res = await fetch(`${API_URL}/service-delivery/clients/${clientId}/briefing/extract`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error al analizar el archivo'); }
    return res.json();
  },
  updateSection: (clientId, section, data) => apiCall(`/service-delivery/clients/${clientId}/section`, {
    method: 'PUT',
    body: JSON.stringify({ section, data })
  }),
  deleteClient: (clientId) => apiCall(`/service-delivery/clients/${clientId}`, {
    method: 'DELETE'
  }),
  inviteToPortal: (clientId) => apiCall(`/service-delivery/clients/${clientId}/invite`, {
    method: 'POST',
    body: JSON.stringify({})
  }),
  uploadFile: async (clientId, section, file) => {
    const authData = JSON.parse(localStorage.getItem('karma-auth') || '{}');
    const token = authData?.state?.token;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('section', section);
    
    const response = await fetch(`${API_URL}/service-delivery/clients/${clientId}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Error subiendo archivo' }));
      throw new Error(error.detail || 'Error uploading file');
    }
    
    return response.json();
  },
  deleteFile: (clientId, fileId, section) => apiCall(`/service-delivery/clients/${clientId}/files/${fileId}?section=${section}`, {
    method: 'DELETE'
  }),
  getFileUrl: (filename) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    return `${API_URL}/service-delivery/files/${filename}?auth=${encodeURIComponent(token)}`;
  },
};

// Objectives API
export const objectivesAPI = {
  get: () => apiCall('/objectives'),
  update: (data) => apiCall('/objectives', {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  getKPIs: () => apiCall('/objectives/kpis')
};

// Client Portal API (client-scoped, isolated server-side)
export const gdriveAPI = {
  status: () => apiCall('/gdrive/status'),
  login: () => apiCall('/gdrive/login'),
  disconnect: () => apiCall('/gdrive/disconnect', { method: 'POST' }),
  saveConfig: (root_folder, subfolders) => apiCall('/gdrive/config', { method: 'PUT', body: JSON.stringify({ root_folder, subfolders }) }),
  createForClient: (clientId) => apiCall(`/gdrive/clients/${clientId}/create`, { method: 'POST' }),
  createMissing: () => apiCall('/gdrive/create-missing', { method: 'POST' }),
  syncSharedRoot: (clientId, url) => apiCall(`/gdrive/clients/${clientId}/shared-root`, { method: 'POST', body: JSON.stringify({ url: url || null }) }),
  unlinkSharedRoot: (clientId) => apiCall(`/gdrive/clients/${clientId}/shared-root`, { method: 'DELETE' }),
  linkOurFolder: (clientId, url) => apiCall(`/gdrive/clients/${clientId}/our-folder`, { method: 'POST', body: JSON.stringify({ url }) }),
};

export const internalCommentsAPI = {
  list: (contentId) => apiCall(`/internal-comments/content/${contentId}`),
  add: (contentId, texto, mentions) => apiCall(`/internal-comments/content/${contentId}`, { method: 'POST', body: JSON.stringify({ texto, mentions }) }),
  edit: (id, texto, mentions) => apiCall(`/internal-comments/${id}`, { method: 'PUT', body: JSON.stringify({ texto, mentions }) }),
  remove: (id) => apiCall(`/internal-comments/${id}`, { method: 'DELETE' }),
  unread: (clientId) => apiCall(`/internal-comments/unread${clientId ? `?client_id=${encodeURIComponent(clientId)}` : ''}`),
};

export const metaSocialAPI = {
  status: () => apiCall('/meta-social/status'),
  accounts: () => apiCall('/meta-social/accounts'),
  setAccount: (cid, page_id, enabled) => apiCall(`/meta-social/clients/${cid}`, { method: 'PUT', body: JSON.stringify({ page_id, enabled }) }),
  enabledClients: () => apiCall('/meta-social/enabled-clients'),
  getPost: (pid) => apiCall(`/meta-social/posts/${pid}`),
  schedule: (pid, data) => apiCall(`/meta-social/posts/${pid}/schedule`, { method: 'POST', body: JSON.stringify(data) }),
  cancel: (pid) => apiCall(`/meta-social/posts/${pid}/cancel`, { method: 'POST' }),
  move: (pid, fecha) => apiCall(`/meta-social/posts/${pid}/move`, { method: 'POST', body: JSON.stringify({ fecha }) }),
  runNow: () => apiCall('/meta-social/run-now', { method: 'POST' }),
  comments: (params) => apiCall(`/meta-social/comments?${new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString()}`),
  syncComments: (cid) => apiCall(`/meta-social/comments/sync?client_id=${cid}`, { method: 'POST' }),
  reply: (id, texto) => apiCall(`/meta-social/comments/${id}/reply`, { method: 'POST', body: JSON.stringify({ texto }) }),
  hide: (id, hidden) => apiCall(`/meta-social/comments/${id}/hide`, { method: 'POST', body: JSON.stringify({ hidden }) }),
  removeComment: (id) => apiCall(`/meta-social/comments/${id}`, { method: 'DELETE' }),
  conversations: (cid, network) => apiCall(`/meta-social/conversations?client_id=${cid}&network=${network}`),
  messages: (convId, cid) => apiCall(`/meta-social/conversations/${convId}/messages?client_id=${cid}`),
  sendDM: (data) => apiCall('/meta-social/messages', { method: 'POST', body: JSON.stringify(data) }),
  inbox: (network) => apiCall(`/meta-social/inbox${network ? `?network=${network}` : ''}`),
  dismiss: (id, value) => apiCall(`/meta-social/comments/${id}/dismiss`, { method: 'POST', body: JSON.stringify({ value }) }),
  access: () => apiCall('/meta-social/access'),
  myAccess: () => apiCall('/meta-social/my-access'),
  setAccess: (uid, value) => apiCall(`/meta-social/access/${uid}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  permissions: () => apiCall('/meta-social/permissions'),
};

export const metaAdsHealthAPI = {
  get: (days = 15) => apiCall(`/meta/api-health?days=${days}`),
};

export const adHooksAPI = {
  list: () => apiCall('/ad-hooks'),
  create: (d) => apiCall('/ad-hooks', { method: 'POST', body: JSON.stringify(d) }),
  update: (id, d) => apiCall(`/ad-hooks/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  remove: (id) => apiCall(`/ad-hooks/${id}`, { method: 'DELETE' }),
  addType: (name) => apiCall('/ad-hooks/types', { method: 'POST', body: JSON.stringify({ name }) }),
};

export const reelsBankAPI = {
  list: () => apiCall('/reels-bank'),
  create: (d) => apiCall('/reels-bank', { method: 'POST', body: JSON.stringify(d) }),
  update: (id, d) => apiCall(`/reels-bank/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  remove: (id) => apiCall(`/reels-bank/${id}`, { method: 'DELETE' }),
  addSector: (name) => apiCall('/reels-bank/sectors', { method: 'POST', body: JSON.stringify({ name }) }),
};

export const checklistsAPI = {
  get: (kind, cid) => apiCall(`/checklists/${kind}/${cid}`),
  setItem: (kind, cid, itemId, hecho) => apiCall(`/checklists/${kind}/${cid}/items/${itemId}`, { method: 'PUT', body: JSON.stringify({ hecho }) }),
  addCustom: (kind, cid, fase_id, texto) => apiCall(`/checklists/${kind}/${cid}/custom`, { method: 'POST', body: JSON.stringify({ fase_id, texto }) }),
  delCustom: (kind, cid, itemId) => apiCall(`/checklists/${kind}/${cid}/custom/${itemId}`, { method: 'DELETE' }),
  resetPhase: (kind, cid, faseId) => apiCall(`/checklists/${kind}/${cid}/phases/${faseId}/reset`, { method: 'POST' }),
  resetAll: (kind, cid) => apiCall(`/checklists/${kind}/${cid}/reset`, { method: 'POST' }),
  setMonth: (kind, cid, mes) => apiCall(`/checklists/${kind}/${cid}/month`, { method: 'PUT', body: JSON.stringify({ mes }) }),
  getServices: (cid) => apiCall(`/checklists/services/${cid}`),
  setServices: (cid, servicios) => apiCall(`/checklists/services/${cid}`, { method: 'PUT', body: JSON.stringify({ servicios }) }),
};

const j = (method, data) => ({ method, body: data === undefined ? undefined : JSON.stringify(data) });
export const adsApprovalsAPI = {
  tree: (cid) => apiCall(`/ads-approvals/clients/${cid}`),
  getSettings: () => apiCall('/ads-approvals/settings'),
  saveSettings: (responsable_id) => apiCall('/ads-approvals/settings', j('PUT', { responsable_id })),
  createCampaign: (cid, data) => apiCall(`/ads-approvals/clients/${cid}/campaigns`, j('POST', data)),
  updateCampaign: (id, data) => apiCall(`/ads-approvals/campaigns/${id}`, j('PUT', data)),
  deleteCampaign: (id) => apiCall(`/ads-approvals/campaigns/${id}`, j('DELETE')),
  createBatch: (campId, nombre = '') => apiCall(`/ads-approvals/campaigns/${campId}/batches`, j('POST', { nombre })),
  renameBatch: (id, nombre) => apiCall(`/ads-approvals/batches/${id}`, j('PUT', { nombre })),
  deleteBatch: (id) => apiCall(`/ads-approvals/batches/${id}`, j('DELETE')),
  sendBatch: (id) => apiCall(`/ads-approvals/batches/${id}/send`, j('POST')),
  addCreative: (batchId, data) => apiCall(`/ads-approvals/batches/${batchId}/creatives`, j('POST', data)),
  updateCreative: (id, data) => apiCall(`/ads-approvals/creatives/${id}`, j('PUT', data)),
  deleteCreative: (id) => apiCall(`/ads-approvals/creatives/${id}`, j('DELETE')),
  resendCreative: (id) => apiCall(`/ads-approvals/creatives/${id}/resend`, j('POST')),
  teamObservation: (id, texto) => apiCall(`/ads-approvals/creatives/${id}/observations`, j('POST', { texto })),
  mine: () => apiCall('/ads-approvals/me'),
  pending: () => apiCall('/ads-approvals/me/pending'),
  saveDiscord: (url) => apiCall('/ads-approvals/settings/discord', j('PUT', { url })),
  approve: (id) => apiCall(`/ads-approvals/me/creatives/${id}/approve`, j('POST')),
  requestChanges: (id, texto) => apiCall(`/ads-approvals/me/creatives/${id}/request-changes`, j('POST', { texto })),
};

export const clientWelcomeAPI = {
  getSettings: () => apiCall('/client-welcome/settings'),
  saveSettings: (data) => apiCall('/client-welcome/settings', { method: 'PUT', body: JSON.stringify(data) }),
  getClient: (id) => apiCall(`/client-welcome/clients/${id}`),
  saveClient: (id, data) => apiCall(`/client-welcome/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  setClientStep: (id, stepId, hecho) => apiCall(`/client-welcome/clients/${id}/steps/${stepId}`, { method: 'PUT', body: JSON.stringify({ hecho }) }),
  me: () => apiCall('/client-welcome/me'),
  setStep: (stepId, hecho) => apiCall(`/client-welcome/me/steps/${stepId}`, { method: 'PUT', body: JSON.stringify({ hecho }) }),
  setDiscord: (hecho) => apiCall('/client-welcome/me/discord', { method: 'PUT', body: JSON.stringify({ hecho }) }),
  setPago: (forma_pago) => apiCall('/client-welcome/me/pago', { method: 'PUT', body: JSON.stringify({ forma_pago }) }),
};

export const timerAPI = {
  me: () => apiCall('/timer/me'),
  start: (taskId) => apiCall('/timer/start', { method: 'POST', body: JSON.stringify({ task_id: taskId }) }),
  stop: () => apiCall('/timer/stop', { method: 'POST' }),
  keepalive: () => apiCall('/timer/keepalive', { method: 'POST' }),
  myLogs: () => apiCall('/timer/my-logs'),
  manualLog: (data) => apiCall('/timer/manual-log', { method: 'POST', body: JSON.stringify(data) }),
};

export const previewSyncAPI = {
  preview: () => apiCall('/admin/preview-sync'),
  apply: () => apiCall('/admin/preview-sync/apply', { method: 'POST' }),
};


export const teamAccessAPI = {
  invite: (data) => apiCall('/auth/invite', { method: 'POST', body: JSON.stringify(data) }),
  sendAccess: (userId) => apiCall(`/auth/resend-invite/${userId}`, { method: 'POST' }),
  listUsers: () => apiCall('/admin/users'),
  updateUser: (userId, data) => apiCall(`/admin/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),
};

export const clientPortalAPI = {
  uploadMyDocument: async (file) => {
    if (isClientViewMode()) throw new Error(READ_ONLY_MSG);
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/client-portal/documents/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo el archivo'); }
    return res.json();
  },
  getBriefing: () => apiCall('/client-portal/briefing'),
  getDriveFolders: () => apiCall('/client-portal/drive-folders'),
  getProfile: () => apiCall('/client-portal/profile'),
  getMeetings: () => apiCall('/client-portal/meetings'),
  saveBriefing: (payload) => apiCall('/client-portal/briefing', {
    method: 'PUT',
    body: JSON.stringify(payload)
  }),
  setBriefingStatus: (status) => apiCall('/client-portal/briefing/status', {
    method: 'PUT',
    body: JSON.stringify({ status })
  }),
  uploadBriefingFile: async (file) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/client-portal/briefing/upload`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo archivo'); }
    return res.json();
  },
  getDocuments: (categoria) => apiCall(`/client-portal/documents${categoria ? `?categoria=${categoria}` : ''}`),
  getInvoices: () => apiCall('/client-portal/invoices'),
  getCampaigns: (month) => apiCall(`/client-portal/campaigns${month ? `?month=${month}` : ''}`),
  getCampaignsHistory: (months = 6) => apiCall(`/client-portal/campaigns/history?months=${months}`),
  getTypedReports: (rtype) => apiCall(`/client-portal/reports/${rtype}`),
  getTypedReport: (rtype, month) => apiCall(`/client-portal/reports/${rtype}/${month}`),
  reportPdfUrl: (rtype, month, clientId = null) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    return `${API_URL}/client-portal/reports/${rtype}/${month}/pdf?auth=${encodeURIComponent(token)}${clientId ? `&client_id=${clientId}` : ''}`;
  },
  docDownloadUrl: (id) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    return `${API_URL}/client-documents/${id}/download?auth=${encodeURIComponent(token)}`;
  },
  getPlanFiles: () => apiCall('/client-portal/plan-files'),
  planFileUrl: (id) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    return `${API_URL}/client-portal/plan-files/${id}/download?auth=${encodeURIComponent(token)}`;
  },
  invoicePdfUrl: (id) => {
    const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
    return `${API_URL}/client-portal/invoices/${id}/pdf?auth=${encodeURIComponent(token)}`;
  },
};

// Builds an authenticated download URL for a briefing file
export const briefingFileUrl = (fileId) => {
  const token = JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';
  return `${process.env.REACT_APP_BACKEND_URL}/api/briefing/files/${fileId}/download?auth=${encodeURIComponent(token)}`;
};


const _authToken = () => JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';

export const socialProofAPI = {
  list: (clientId) => apiCall(`/service-delivery/clients/${clientId}/social-proof`),
  create: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/social-proof`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/service-delivery/social-proof/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/service-delivery/social-proof/${id}`, { method: 'DELETE' }),
  setDriveLink: (clientId, drive_link) => apiCall(`/service-delivery/clients/${clientId}/social-proof/drive-link`, {
    method: 'PUT', body: JSON.stringify({ drive_link }),
  }),
  uploadMedia: async (clientId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/service-delivery/clients/${clientId}/social-proof/upload`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${_authToken()}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo archivo'); }
    return res.json();
  },
  mediaUrl: (filename) => `${process.env.REACT_APP_BACKEND_URL}/api/service-delivery/social-proof/media/${filename}?auth=${encodeURIComponent(_authToken())}`,
};

export const brandKitAPI = {
  get: (clientId) => apiCall(`/service-delivery/clients/${clientId}/brand-kit`),
  save: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/brand-kit`, {
    method: 'PUT', body: JSON.stringify({ data }),
  }),
  uploadFile: async (clientId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/service-delivery/clients/${clientId}/brand-kit/upload`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${_authToken()}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo archivo'); }
    return res.json();
  },
  fileUrl: (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/briefing/files/${fileId}/download?auth=${encodeURIComponent(_authToken())}`,
  extractColors: async (clientId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/service-delivery/clients/${clientId}/brand-kit/extract-colors`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${_authToken()}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'No se pudieron extraer colores'); }
    return res.json();
  },
};


export const planAPI = {
  // Motor único de extracción del Plan de Social Media. target: 'buyer_persona' | 'competencia' | ...
  extract: (clientId, target) => apiCall(`/service-delivery/clients/${clientId}/plan/extract`, {
    method: 'POST', body: JSON.stringify({ target }),
  }),
};

export const marketResearchAPI = {
  get: (clientId) => apiCall(`/service-delivery/clients/${clientId}/market-research`),
  save: (clientId, data) => apiCall(`/service-delivery/clients/${clientId}/market-research`, {
    method: 'PUT', body: JSON.stringify({ data }),
  }),
};

// Catálogo de Producto (ofertas comerciales de la agencia)
export const productCatalogAPI = {
  get: () => apiCall('/product-catalog'),
  save: (data) => apiCall('/product-catalog', {
    method: 'PUT', body: JSON.stringify(data),
  }),
};

// Legal — archivo documental de la empresa (solo CEOs)
const getToken = () => JSON.parse(localStorage.getItem('karma-auth') || '{}')?.state?.token || '';

export const legalAPI = {
  getData: () => apiCall('/legal/data'),
  addCategory: (nombre) => apiCall('/legal/categories', {
    method: 'POST', body: JSON.stringify({ nombre }),
  }),
  deleteCategory: (name) => apiCall(`/legal/categories/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  }),
  upload: async ({ file, nombre, categoria, nota }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('nombre', nombre || '');
    fd.append('categoria', categoria || '');
    fd.append('nota', nota || '');
    const res = await fetch(`${API_URL}/legal/documents`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo el documento'); }
    return res.json();
  },
  update: (id, data) => apiCall(`/legal/documents/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/legal/documents/${id}`, { method: 'DELETE' }),
  sendEmail: (id, data) => apiCall(`/legal/documents/${id}/email`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  downloadUrl: (id) => `${API_URL}/legal/documents/${id}/download?auth=${encodeURIComponent(getToken())}`,
};

// Recursos (generales del equipo + privados por miembro)
export const resourcesAPI = {
  getAll: () => apiCall('/resources'),
  move: (id, scope) => apiCall(`/resources/${id}/move`, { method: 'POST', body: JSON.stringify({ scope }) }),
  teamMembers: () => apiCall('/resources/team-members'),
  assign: (data) => apiCall('/resources/assign', { method: 'POST', body: JSON.stringify(data) }),
  assigned: () => apiCall('/resources/assigned'),
  uploadDocImage: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/resources/doc-images`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: fd });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.detail || 'Error subiendo la imagen');
    return j;
  },
  create: (data) => apiCall('/resources', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/resources/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => apiCall(`/resources/${id}`, { method: 'DELETE' }),
  upload: async ({ file, scope, titulo, categoria, tags, assignTo }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('scope', scope);
    fd.append('titulo', titulo || '');
    fd.append('categoria', categoria || '');
    fd.append('tags', tags || '');
    fd.append('assign_to', (assignTo || []).join(','));
    const res = await fetch(`${API_URL}/resources/upload`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: fd,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Error subiendo el archivo'); }
    return res.json();
  },
  downloadUrl: (id) => `${API_URL}/resources/${id}/download?auth=${encodeURIComponent(getToken())}`,
};

// Incidencias de Cobro (registro interno de excepciones de facturación, NO toca Quipu)
export const billingIncidentsAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiCall(`/billing-incidents${query ? `?${query}` : ''}`);
  },
  create: (data) => apiCall('/billing-incidents', {
    method: 'POST', body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/billing-incidents/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/billing-incidents/${id}`, { method: 'DELETE' }),
};

export const leadMeetingsAPI = {
  getTemplate: () => apiCall('/lead-meetings/template'),
  saveTemplate: (bloques) => apiCall('/lead-meetings/template', { method: 'PUT', body: JSON.stringify({ bloques }) }),
  getCeos: () => apiCall('/lead-meetings/ceos'),
  list: (leadKey) => apiCall(`/lead-meetings?lead_key=${encodeURIComponent(leadKey)}`),
  create: (data) => apiCall('/lead-meetings', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/lead-meetings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => apiCall(`/lead-meetings/${id}`, { method: 'DELETE' }),
};
